/**
 * Executive Report page (mockup carbon/01: brief, tiles and trend chart;
 * blueprint §5.2 reports).
 *
 * Composition (top to bottom):
 *   1. ExecutiveStandfirst: the action-first h1, the lede and the provenance
 *      line (BriefHero), with the view's actions (Download PDF, the one
 *      primary button, and the reporting-period select), beside the Guardian
 *      Score tile (verdict, period delta tag, the 100-point waffle).
 *   2. The four component tiles in a condensed grid (mockup 01's pillar
 *      row): score, period delta, what the score measures, and its points
 *      toward the Guardian Score on a bar in the pillar's mark color.
 *   3. Activity this period: the frozen KpiStrip (drift remediated,
 *      violations resolved, open critical) and a helper line with the other
 *      three report metrics.
 *   4. FrameworkRoll: framework tiles, weakest first.
 *   5. The lower row: the board summary (NarrativeBlock) beside the period
 *      trend per pillar (ExecutiveTrendChart). They stack below 1056px.
 *
 * Loading: while `useExecutiveReport.isLoading` is true the BuildingLoader
 * (the composer's loading brief, timer-driven steps) stands in for the brief
 * and body, and keeps the actions in place. Backend generation is
 * synchronous (about 10 to 30 seconds).
 *
 * Copy reaches the page through composeExecutiveReportBrief and BriefHero
 * (blueprint §4.7); `narrativeDeck` stays the report body's framing (§6.2
 * row 8). There is no print path (§5.5): the PDF is generated server-side.
 *
 * Hard-preserved (regression surface): the Download PDF action and its
 * `api.downloadExecutiveReport` call, the period select (30/60/90 days),
 * tenant context auto-sync from the side nav.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTenantContext } from '@/hooks/useTenantContext'
import { useExecutiveReport } from '@/hooks/useExecutiveReport'
import { api } from '@/lib/api'
import {
  composeExecutiveReportBrief,
  executiveReportToBriefInput,
} from '@/lib/executive-report-brief'
import type { ExecutiveReportData } from '@/types/executiveReport'

import {
  Button,
  CondensedGrid,
  IconArrowRight,
  IconDownload,
  ScoreBar,
  Select,
  Tag,
  type BriefHeroButtonAction,
} from '@/components/carbon'
import ExecutiveStandfirst from '@/components/ExecutiveReport/ExecutiveStandfirst'
import BuildingLoader from '@/components/ExecutiveReport/BuildingLoader'
import FrameworkRoll from '@/components/ExecutiveReport/FrameworkRoll'
import ExecutiveTrendChart from '@/components/ExecutiveReport/ExecutiveTrendChart'
import NarrativeBlock from '@/components/ExecutiveReport/NarrativeBlock'
import {
  componentTiles,
  guardianScoreDelta,
  reportProvenance,
  reportSegments,
  reportTrendRows,
} from '@/components/ExecutiveReport/report-view'
import KpiStrip, { type KpiCell } from '@/components/PostureBrief/KpiStrip'
import { PillarTile } from '@/components/Dashboard/v2/PillarShell'

const PERIOD_OPTIONS = [
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
] as const

export default function ExecutiveReport() {
  const { defaultTenantId, userTenants, isLoading: contextLoading } = useTenantContext()
  // Start on the context's tenant when it is already known, so the first
  // paint is the report (or its loader), not a one-frame "choose a tenant".
  const [selectedTenantId, setSelectedTenantId] = useState<string | undefined>(() =>
    contextLoading ? undefined : defaultTenantId,
  )
  const [periodDays, setPeriodDays] = useState<number>(30)
  const [isDownloading, setIsDownloading] = useState(false)

  // Sync selected tenant from context on first load.
  useEffect(() => {
    if (!contextLoading && defaultTenantId) {
      setSelectedTenantId(defaultTenantId)
    }
  }, [contextLoading, defaultTenantId])

  const { data: report, isLoading, error, refetch } = useExecutiveReport(selectedTenantId, periodDays)

  const tenantName = useMemo(() => {
    if (!selectedTenantId || !userTenants) return ''
    // userTenants is intentionally typed as any[] in the context — the
    // narrowing here matches the legacy lookup pattern.
    return (
      (userTenants as { tenantId: string; name?: string }[]).find(
        t => t.tenantId === selectedTenantId,
      )?.name ?? ''
    )
  }, [selectedTenantId, userTenants])

  const handleDownload = async () => {
    if (!selectedTenantId) return
    setIsDownloading(true)
    try {
      const blob = await api.downloadExecutiveReport(selectedTenantId, 'pdf', periodDays)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `executive-report-${tenantName || 'tenant'}-${new Date().toISOString().split('T')[0]}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      // We surface failures via the existing toast layer elsewhere; logging
      // the error here keeps the diagnostic without crashing the page.
      console.error('Failed to download report:', err)
    } finally {
      setIsDownloading(false)
    }
  }

  /* --------------- Render guards --------------- */

  if (contextLoading) {
    return <BuildingLoader periodDays={periodDays} />
  }

  if (!selectedTenantId) {
    return (
      <PageNotice
        id="exec-no-tenant"
        headline="Choose a tenant to see its report."
        lede={
          <>
            <b className="font-semibold text-text-primary">The report covers one tenant at a time</b>, so pick one in the
            tenant switcher at the top of the side nav.
          </>
        }
      />
    )
  }

  if (error) {
    return (
      <PageNotice
        id="exec-error"
        headline="Reload the Executive Report."
        lede={
          <>
            <b className="font-semibold text-text-primary">The report data did not load</b>, so Guardian has nothing to
            summarize yet.
          </>
        }
        action={
          <Button kind="primary" className="mt-8" icon={<IconArrowRight />} onClick={() => refetch()}>
            Retry
          </Button>
        }
      />
    )
  }

  // The view's one primary button, drawn by BriefHero in place of a route
  // link. Disabled until the report lands: the PDF is generated from it.
  const downloadAction: BriefHeroButtonAction = {
    label: isDownloading ? 'Preparing the PDF' : 'Download PDF',
    onClick: handleDownload,
    loading: isDownloading,
    disabled: isLoading,
    icon: <IconDownload />,
    'aria-label': 'Download executive report as PDF',
  }

  // A view control, not an action: BriefHero keeps it while the brief loads,
  // so the period can change before the report lands.
  const periodSelect = (
    <Select
      label="Reporting period"
      hideLabel
      aria-label="Reporting period"
      size="lg"
      value={String(periodDays)}
      onChange={e => setPeriodDays(Number(e.target.value))}
      options={PERIOD_OPTIONS.map(opt => ({ value: String(opt.value), label: opt.label }))}
      wrapperClassName="w-48"
    />
  )

  if (isLoading) {
    return <BuildingLoader periodDays={periodDays} primaryAction={downloadAction} controls={periodSelect} />
  }

  if (!report) return null

  // A report with no Guardian Score has nothing to summarize: the tenant has
  // not been assessed yet. Say so instead of rendering the composer's
  // "compiling" brief over an empty report.
  if (report.currentGuardianScore == null) {
    return (
      <PageNotice
        id="exec-no-data"
        headline="Check back after the first assessment."
        lede={
          <>
            <b className="font-semibold text-text-primary">This tenant has no Guardian Score yet</b>, so the report has
            nothing to summarize.
          </>
        }
      />
    )
  }

  /* --------------- Brief composition --------------- */

  const brief = composeExecutiveReportBrief(
    executiveReportToBriefInput(report, {
      tenantId: selectedTenantId,
      tenantName,
      periodDays,
    }),
  )

  /* --------------- Render --------------- */

  return (
    <div>
      <ExecutiveStandfirst
        brief={brief}
        score={report.currentGuardianScore}
        segments={reportSegments(report)}
        delta={guardianScoreDelta(report, periodDays)}
        primaryAction={downloadAction}
        controls={periodSelect}
        provenance={reportProvenance({
          generatedAt: report.generatedAt,
          tenantName: tenantName || report.tenantName,
          days: periodDays,
        })}
      />

      <CondensedGrid
        as="section"
        aria-label="Guardian Score components"
        columns={{ base: 1, 'c-md': 2, 'c-xlg': 4 }}
        className="mt-8"
      >
        {componentTiles(report, periodDays).map(tile => (
          <PillarTile
            key={tile.category}
            title={tile.title}
            category={tile.category}
            to={tile.to}
            linkLabel={tile.linkLabel}
            score={tile.score}
            delta={tile.delta ? <Tag className="tabular-nums">{tile.delta}</Tag> : undefined}
            finding={tile.definition}
            viz={
              tile.caption && tile.score != null ? (
                <>
                  <p className="m-0 mb-2 text-helper text-text-helper">{tile.caption}</p>
                  <ScoreBar value={tile.score} category={tile.category} label={`${tile.title} score`} />
                </>
              ) : (
                <p className="m-0 text-helper text-text-helper">No data for this period yet.</p>
              )
            }
          />
        ))}
      </CondensedGrid>

      <section aria-labelledby="exec-activity-heading" className="mt-12">
        <SectionHead
          id="exec-activity-heading"
          title="Activity this period"
          description={`Remediation and open items over the last ${periodDays} days.`}
        />
        <KpiStrip tone="compliance" cells={buildKpiCells(report, periodDays)} />
        <SecondaryMetricsLine report={report} />
      </section>

      <section aria-labelledby="exec-frameworks-heading" className="mt-12">
        <SectionHead
          id="exec-frameworks-heading"
          title="Frameworks"
          description="Each starred framework's score, weakest first. Frameworks marked In score count toward your Guardian Score."
        />
        <FrameworkRoll frameworks={report.frameworkScores} periodDays={periodDays} />
      </section>

      <div className="mt-12 grid grid-cols-1 gap-8 c-lg:grid-cols-2">
        <NarrativeBlock
          narrative={report.executiveNarrative}
          deck={brief.narrativeDeck}
          tenantName={tenantName || 'your tenant'}
          periodDays={periodDays}
        />
        <ExecutiveTrendChart data={reportTrendRows(report)} periodDays={periodDays} />
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

/** A section's heading (1.25rem/400) over a one-line secondary description, as the DataTable head reads. */
function SectionHead({ id, title, description }: { id: string; title: string; description: string }) {
  return (
    <div className="mb-4">
      <h2 id={id} className="m-0 text-heading font-normal text-text-primary">
        {title}
      </h2>
      <p className="m-0 mt-0.5 text-body text-text-secondary">{description}</p>
    </div>
  )
}

/**
 * A whole-page state with no report to show (no tenant, a failed load, no
 * Guardian Score yet): the action-first h1 and one lede, the same shape as
 * the brief it stands in for.
 */
function PageNotice({ id, headline, lede, action }: { id: string; headline: string; lede: ReactNode; action?: ReactNode }) {
  return (
    <section aria-labelledby={id} className="mt-8 max-w-[56rem]">
      <h1 id={id} className="m-0 text-headline-sm font-light text-text-primary max-c-md:text-[2rem]">
        {headline}
      </h1>
      <p className="m-0 mt-6 max-w-[56ch] text-heading font-light text-text-secondary">{lede}</p>
      {action}
    </section>
  )
}

/**
 * Build the three KPI cells. Drift remediated, violations resolved and open
 * critical answer the three questions an executive scans for: what got
 * fixed, what got cleared, and what is still open at critical severity. The
 * other three metrics surface in the helper line below the strip.
 */
function buildKpiCells(report: ExecutiveReportData, periodDays: number): KpiCell[] {
  const m = report.metrics

  return [
    {
      id: 'drift-remediated',
      numeral: String(m.driftItemsRemediated),
      label: 'Drift remediated',
      stat: pluralizeItems(m.driftItemsRemediated, 'configuration drift closed', 'configuration drifts closed'),
      trend: `Across the **${periodDays}-day** window`,
      severity: 'tone',
    },
    {
      id: 'violations-resolved',
      numeral: String(m.violationsResolved),
      label: 'Violations resolved',
      stat: pluralizeItems(m.violationsResolved, 'compliance gap closed', 'compliance gaps closed'),
      trend: `Across the **${periodDays}-day** window`,
      severity: 'tone',
    },
    {
      id: 'open-critical',
      numeral: String(m.openCriticalViolations),
      label: 'Open critical',
      stat:
        m.openCriticalViolations === 0
          ? 'Nothing critical outstanding'
          : pluralizeItems(
              m.openCriticalViolations,
              'critical item still open',
              'critical items still open',
            ),
      trend: openCriticalTrend(m.openCriticalViolations),
      // Critical severity takes the octagon whenever any item is open (Shape-First Severity Rule).
      severity: m.openCriticalViolations > 0 ? 'crit' : 'tone',
    },
  ]
}

function openCriticalTrend(n: number): string {
  if (n === 0) return 'No critical items open.'
  return n === 1 ? 'Review the one open critical item.' : `Review the ${n} open critical items.`
}

function pluralizeItems(n: number, singular: string, plural: string): string {
  if (n === 0) return `0 ${plural}`
  if (n === 1) return `1 ${singular}`
  return `${n} ${plural}`
}

/** The three report metrics the KPI strip has no tile for, as one helper line. */
function SecondaryMetricsLine({ report }: { report: ExecutiveReportData }) {
  const m = report.metrics
  const items: { label: string; value: number }[] = [
    { label: 'Drift detected', value: m.totalDriftDetected },
    { label: 'Waivers granted', value: m.waiversGranted },
    { label: 'Open high', value: m.openHighViolations },
  ]
  return (
    <dl className="-mt-4 mb-0 flex flex-wrap gap-x-6 gap-y-1 text-helper text-text-helper">
      {items.map(it => (
        <div key={it.label} className="flex items-baseline gap-1.5">
          <dt>{it.label}</dt>
          <dd className="m-0 font-semibold tabular-nums text-text-primary">{it.value}</dd>
        </div>
      ))}
    </dl>
  )
}
